import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

const API = process.env.REACT_APP_API_URL || 'https://api.daggpt.network';
const TOKEN_KEY = 'daggpt_token';
const USER_KEY  = 'daggpt_user';

/**
 * SSOPage - /sso
 * Handles incoming SSO jumps from DAGARMY or DAGCHAIN.
 * URL format: /sso?token=<sso_jwt>
 * Calls the backend to validate the SSO token and issues a DAGGPT platform JWT.
 */
export default function SSOPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [message, setMessage] = useState('Verifying your session…');

  useEffect(() => {
    const ssoToken = searchParams.get('token');

    if (!ssoToken) {
      setStatus('error');
      setMessage('No SSO token provided.');
      setTimeout(() => navigate('/'), 2000);
      return;
    }

    (async () => {
      try {
        const res = await fetch(`${API}/api/auth/sso/validate`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ token: ssoToken }),
        });

        const data = await res.json();

        if (!res.ok || !data.token || !data.user) {
          throw new Error(data.error || 'SSO validation failed');
        }

        localStorage.setItem(TOKEN_KEY, data.token);
        localStorage.setItem(USER_KEY, JSON.stringify(data.user));

        setStatus('success');
        setMessage(`Welcome, ${data.user.full_name || data.user.email}!`);
        setTimeout(() => navigate('/dashboard', { replace: true }), 800);

      } catch (err) {
        console.error('[DAGGPT SSO] Validation failed:', err);
        setStatus('error');
        setMessage(err.message || 'Session transfer failed. Please log in.');
        setTimeout(() => navigate('/'), 2500);
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #0a0a0f 0%, #0f0f1a 50%, #0a0a0f 100%)',
    }}>
      <div style={{ textAlign: 'center', color: '#e2e8f0', padding: '24px' }}>
        {status === 'loading' && (
          <>
            <div style={{
              width: '56px', height: '56px', margin: '0 auto 24px',
              border: '3px solid rgba(139,92,246,0.3)',
              borderTopColor: '#8b5cf6',
              borderRadius: '50%',
              animation: 'spin 0.8s linear infinite',
            }} />
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            <div style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>
              Transferring your session
            </div>
            <p style={{ fontSize: '14px', color: '#64748b' }}>{message}</p>
          </>
        )}
        {status === 'success' && (
          <>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🚀</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#10b981', marginBottom: '8px' }}>
              Session transferred!
            </div>
            <p style={{ fontSize: '14px', color: '#64748b' }}>{message}</p>
          </>
        )}
        {status === 'error' && (
          <>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>⚠️</div>
            <div style={{ fontSize: '18px', fontWeight: '600', color: '#ef4444', marginBottom: '8px' }}>
              Session transfer failed
            </div>
            <p style={{ fontSize: '14px', color: '#64748b' }}>{message}</p>
          </>
        )}
      </div>
    </div>
  );
}
